import "../../../App.css";
import React, { useState, useEffect, useMemo } from "react";
import CenterSpinner from "../../../components/CenterSpinner";
import { restApi } from "../../../util/api";
import { error_message } from "../../../util/messages";
import { Table, Input, Row, Col, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";

interface PatientActivityData {
  creation_date: string | null;
  curp: string | null;
  diagnosis: string | null;
  family_name_father: string;
  himfgid: number | null;
  last_depression_date: string | null;
  last_family_date: string | null;
  last_mobile_sei_date: string | null;
  last_mobile_symptom_date: string | null;
  last_oncology_date: string | null;
  last_sei_date: string | null;
  last_stress_date: string | null;
  last_substance_date: string | null;
  name: string;
  patient_id: string;
  status: string;
}

const PatientActivity: React.FC = () => {
  const [data, setData] = useState<PatientActivityData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");

  const statusOptions = [
    { label: "Todo", value: "all" },
    { label: "Activo", value: "Activo" },
    { label: "Alta", value: "Alta" },
    { label: "Paliativo", value: "Paliativo" },
    { label: "Fallecido", value: "Fallecido" },
    { label: "Recien creado", value: "Recien creado" },
    { label: "Sin dato reciente", value: "Sin dato reciente" },
  ];

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      setIsLoading(true);
      const { data } = await restApi().get<Record<"report", PatientActivityData[]>>("report/hospital-matrix");
      // console.log("Hospital Matrix Data:", data);
      setData(data.report || []);
    } catch (e: any) {
      console.error("Error fetching hospital matrix data:", e);
      error_message(e.response?.data?.reason || "Error al cargar los datos");
      Promise.reject(e);
    } finally {
      setIsLoading(false);
    }
  };

  // Fuzzy search function
  const fuzzyMatch = (text: string, search: string): boolean => {
    if (!text) return false;
    const searchLower = search.toLowerCase();
    const textLower = text.toString().toLowerCase();
    return textLower.includes(searchLower);
  };

  // Filter data based on search and status
  const filteredData = useMemo(() => {
    return data.filter((record) => {
      const matchesStatus = selectedStatus === "all" || record.status === selectedStatus;
      const matchesSearch = !searchText || (
        fuzzyMatch(record.name, searchText) ||
        fuzzyMatch(record.family_name_father, searchText) ||
        fuzzyMatch(record.himfgid?.toString() || "", searchText) ||
        fuzzyMatch(record.curp || "", searchText) ||
        fuzzyMatch(record.patient_id, searchText)
      );
      return matchesStatus && matchesSearch;
    });
  }, [data, searchText, selectedStatus]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return moment(dateString).format("DD/MM/YYYY");
  };

  const columns: ColumnsType<PatientActivityData> = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      fixed: "left",
      width: 120,
    },
    {
      title: "Apellido",
      dataIndex: "family_name_father",
      key: "family_name_father",
      sorter: (a, b) => a.family_name_father.localeCompare(b.family_name_father),
      fixed: "left",
      width: 120,
    },
    {
      title: "HIMFG ID",
      dataIndex: "himfgid",
      key: "himfgid",
      sorter: (a, b) => (a.himfgid || 0) - (b.himfgid || 0),
      width: 120,
      render: (value) => value || "-",
    },
    {
      title: "CURP",
      dataIndex: "curp",
      key: "curp",
      sorter: (a, b) => (a.curp || "").localeCompare(b.curp || ""),
      width: 180,
      render: (value) => value || "-",
    },
    {
      title: "Diagnóstico",
      dataIndex: "diagnosis",
      key: "diagnosis",
      sorter: (a, b) => (a.diagnosis || "").localeCompare(b.diagnosis || ""),
      width: 250,
      render: (value) => (
        <div style={{ 
          whiteSpace: "normal", 
          wordWrap: "break-word",
          wordBreak: "break-word"
        }}>
          {value || "-"}
        </div>
      ),
    },
    {
      title: "Fecha de Creación",
      dataIndex: "creation_date",
      key: "creation_date",
      sorter: (a, b) => {
        if (!a.creation_date) return 1;
        if (!b.creation_date) return -1;
        return new Date(a.creation_date).getTime() - new Date(b.creation_date).getTime();
      },
      width: 130,
      render: formatDate,
    },
    {
      title: "Última Oncología",
      dataIndex: "last_oncology_date",
      key: "last_oncology_date",
      sorter: (a, b) => {
        if (!a.last_oncology_date) return 1;
        if (!b.last_oncology_date) return -1;
        return new Date(a.last_oncology_date).getTime() - new Date(b.last_oncology_date).getTime();
      },
      width: 130,
      render: formatDate,
    },
    {
      title: "Última Depresión",
      dataIndex: "last_depression_date",
      key: "last_depression_date",
      sorter: (a, b) => {
        if (!a.last_depression_date) return 1;
        if (!b.last_depression_date) return -1;
        return new Date(a.last_depression_date).getTime() - new Date(b.last_depression_date).getTime();
      },
      width: 130,
      render: formatDate,
    },
    {
      title: "Última Familia",
      dataIndex: "last_family_date",
      key: "last_family_date",
      sorter: (a, b) => {
        if (!a.last_family_date) return 1;
        if (!b.last_family_date) return -1;
        return new Date(a.last_family_date).getTime() - new Date(b.last_family_date).getTime();
      },
      width: 130,
      render: formatDate,
    },
    {
      title: "Última SEI Móvil",
      dataIndex: "last_mobile_sei_date",
      key: "last_mobile_sei_date",
      sorter: (a, b) => {
        if (!a.last_mobile_sei_date) return 1;
        if (!b.last_mobile_sei_date) return -1;
        return new Date(a.last_mobile_sei_date).getTime() - new Date(b.last_mobile_sei_date).getTime();
      },
      width: 140,
      render: formatDate,
    },
    {
      title: "Última Síntoma Móvil",
      dataIndex: "last_mobile_symptom_date",
      key: "last_mobile_symptom_date",
      sorter: (a, b) => {
        if (!a.last_mobile_symptom_date) return 1;
        if (!b.last_mobile_symptom_date) return -1;
        return new Date(a.last_mobile_symptom_date).getTime() - new Date(b.last_mobile_symptom_date).getTime();
      },
      width: 160,
      render: formatDate,
    },
    {
      title: "Última SEI",
      dataIndex: "last_sei_date",
      key: "last_sei_date",
      sorter: (a, b) => {
        if (!a.last_sei_date) return 1;
        if (!b.last_sei_date) return -1;
        return new Date(a.last_sei_date).getTime() - new Date(b.last_sei_date).getTime();
      },
      width: 130,
      render: formatDate,
    },
    {
      title: "Última Estrés",
      dataIndex: "last_stress_date",
      key: "last_stress_date",
      sorter: (a, b) => {
        if (!a.last_stress_date) return 1;
        if (!b.last_stress_date) return -1;
        return new Date(a.last_stress_date).getTime() - new Date(b.last_stress_date).getTime();
      },
      width: 130,
      render: formatDate,
    },
    {
      title: "Última Sustancia",
      dataIndex: "last_substance_date",
      key: "last_substance_date",
      sorter: (a, b) => {
        if (!a.last_substance_date) return 1;
        if (!b.last_substance_date) return -1;
        return new Date(a.last_substance_date).getTime() - new Date(b.last_substance_date).getTime();
      },
      width: 140,
      render: formatDate,
    },
    {
      title: "Estatus",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
      width: 150,
      fixed: "right" as const,
      render: (value: string) => value || "-",
    },
  ];

  if (isLoading) {
    return <CenterSpinner />;
  }

  return (
    <div
      style={{
        margin: "2rem auto",
        width: "calc(90% + 24px)",
        backgroundColor: "white",
        borderRadius: "0.25rem",
        padding: "2rem",
      }}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <h2>Actividad del Paciente</h2>
          <p>Panel de datos de actividad hospitalaria</p>
        </Col>
        <Col span={6}>
          <Select
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value)}
            options={statusOptions}
            style={{ width: "100%", marginBottom: 16 }}
            size="large"
          />
        </Col>
        <Col span={18}>
          <Input
            placeholder="Buscar por nombre, apellido, HIMFG ID, CURP, o ID de paciente"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ marginBottom: 16 }}
            size="large"
            allowClear
          />
        </Col>
        <Col span={24}>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="patient_id"
            scroll={{ x: 1800 }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} pacientes`,
            }}
          />
        </Col>
      </Row>
    </div>
  );
};

export default PatientActivity;
